import { graphql, useFragment } from "react-relay";
import { Popover, PopoverList } from "../../../../components/mobile/Popover";

import { PropsWithChildren, useCallback, useMemo } from "react";
import {
  AHIcon,
  AHSeparator,
  AHText,
  AHTextFonts,
  Box,
  TouchableBox,
  useBoxStyle,
  useTheme,
  AHTeamIconCircle,
  UserOrEmployeeAvatar,
} from "arkh";
import Color from "color";

import { TeamPickerPopoverItem_team$key } from "./__generated__/TeamPickerPopoverItem_team.graphql";
import { useChangeTeamMembership } from "../../../Home/useChangeTeamMembership";
import { Pressable } from "react-native";
import { onPressInHaptic } from "widgets/src/onPressInHaptic";
import { TeamIcon } from "arkh/src/globalTypes";

import { TeamPickerPopover_store$key } from "./__generated__/TeamPickerPopover_store.graphql";

import { useOptionalUser } from "@/src/OS/Login/State";
import { TeamPickerPopover_storeViewer$key } from "./__generated__/TeamPickerPopover_storeViewer.graphql";

type TeamPickerPopoverProps = {
  onClose(): void;
  onSelectTeamId(teamId: string): void;
  selectedTeamId: string | undefined;
  top: number;
  storeViewer: TeamPickerPopover_storeViewer$key;
  store: TeamPickerPopover_store$key;
};

export function TeamPickerPopover(props: TeamPickerPopoverProps) {
  const storeViewer = useFragment(
    graphql`
      fragment TeamPickerPopover_storeViewer on StoreViewer {
        teams {
          id
          todoActiveAssignedCount(employeeId: $employeeId)
          ...TeamPickerPopoverItem_team
        }
      }
    `,
    props.storeViewer
  );
  const store = useFragment(
    graphql`
      fragment TeamPickerPopover_store on Store {
        currentEmployee(id: $employeeId) {
          ...Avatar_employee
        }
        me {
          teams {
            id
          }
          user {
            ...Avatar_user
          }
        }
      }
    `,
    props.store
  );

  const myTeamIds = store.me?.teams.map((t) => t.id);

  const teams = useMemo(() => {
    const favoriteTeams = storeViewer.teams.flatMap((t) =>
      myTeamIds?.includes(t.id)
        ? [{ type: "item", favorite: false, ...t } as const]
        : []
    );
    const otherTeams = storeViewer.teams.flatMap((t) =>
      !myTeamIds?.includes(t.id)
        ? [{ type: "item", favorite: false, ...t } as const]
        : []
    );

    return [
      ...favoriteTeams.map((t) => ({
        ...t,
        favorite: true,
      })),
      ...(favoriteTeams?.length === 0 || otherTeams.length === 0
        ? []
        : [{ type: "separator" as const }]),
      ...otherTeams,
    ];
  }, [storeViewer.teams, myTeamIds]);

  const { onSelectTeamId, onClose, selectedTeamId, top } = props;

  const employee = store.currentEmployee;

  const renderItem = useCallback(
    ({ item }: { item: (typeof teams)[number] }) => {
      if (item.type === "separator") {
        return <AHSeparator spaceVertical="small" />;
      }

      const assignedCount =
        item.type === "item" ? item.todoActiveAssignedCount : 0;
      return (
        <Item
          onPress={() => {
            onClose();
            setTimeout(() => {
              onSelectTeamId(item.id);
            }, 0);
          }}
          favorite={item.favorite}
          team={item}
          isCurrentTeam={selectedTeamId === item.id}
        >
          <Box
            style={{
              flexDirection: "row",
              alignContent: "center",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {assignedCount > 0 ? (
              <UserOrEmployeeAvatar
                employee={employee}
                user={store.me?.user ?? null}
                size="small"
                style="assigned-to-me"
              />
            ) : null}
          </Box>
        </Item>
      );
    },
    [employee, onClose, onSelectTeamId, selectedTeamId, store.me]
  );

  return (
    <Popover customWidth={300} onClose={onClose} side="left" top={top}>
      <PopoverList renderItem={renderItem} data={teams} />
    </Popover>
  );
}

type ItemComponentProps = {
  team: TeamPickerPopoverItem_team$key;
  isCurrentTeam: boolean;
  favorite: boolean;
  onPress(): void;
};

function Item(props: PropsWithChildren<ItemComponentProps>) {
  const theme = useTheme();
  const team = useFragment(
    graphql`
      fragment TeamPickerPopoverItem_team on Team {
        id
        name
        color
        icon
        isPrivate
      }
    `,
    props.team
  );

  const user = useOptionalUser();

  const { joinTeam, leaveTeam } = useChangeTeamMembership(team.id);

  const op = useCallback(() => {
    if (!props.favorite) {
      joinTeam();
    } else {
      leaveTeam();
    }
  }, [props.favorite, joinTeam, leaveTeam]);

  const starButtonStyle = useBoxStyle({
    spaceRight: "small",
    spaceLeft: "medium",
  });

  return (
    <TouchableBox
      onPress={props.onPress}
      spaceLeft="medium"
      spaceRight="small"
      cornerRadius="medium"
      style={{
        flexDirection: "row",
        flex: 1,
        backgroundColor: props.isCurrentTeam
          ? Color(team.color ?? theme.primary)
              .mix(Color(theme.surface), 0.9)
              .toString()
          : undefined,
      }}
    >
      <Box style={{ flexDirection: "row", alignItems: "center", flex: 1 }}>
        <Box
          spaceVertical="medium"
          style={{ flexDirection: "row", alignItems: "center", flex: 1 }}
        >
          <AHTeamIconCircle
            size="smallMedium"
            type={team.icon ?? TeamIcon.default}
            isPrivate={team.isPrivate}
            color={
              props.isCurrentTeam || props.favorite
                ? (team.color ?? theme.primary)
                : theme["surface-background"]
            }
            iconColor={
              props.isCurrentTeam || props.favorite
                ? "text-inverted"
                : "text-washed"
            }
          />
          <AHText
            style={{ flex: 1 }}
            numberOfLines={1}
            type="title-small"
            minimumFontScale={0.9}
            adjustsFontSizeToFit
            allowFontScaling={false}
            font={
              props.isCurrentTeam || props.favorite
                ? AHTextFonts.BOLD
                : AHTextFonts.REGULAR
            }
            color={props.isCurrentTeam ? "text-primary" : "text-secondary"}
            spaceHorizontal="small"
          >
            {team.name}
          </AHText>
        </Box>
        {props.children}
        {user?.isSharedDevice ? null : (
          <Pressable
            style={[
              starButtonStyle,
              {
                height: "100%",
                justifyContent: "center",
                alignItems: "center",
              },
            ]}
            onPressIn={onPressInHaptic}
            onPress={op}
          >
            {props.favorite ? (
              <AHIcon
                type="star"
                size="medium"
                color="status-inactive"
                customColor={
                  props.isCurrentTeam
                    ? (team.color ?? theme.primary)
                    : undefined
                }
              />
            ) : (
              <AHIcon color="text-washed" type="star-outlined" size="medium" />
            )}
          </Pressable>
        )}
      </Box>
    </TouchableBox>
  );
}
